/**
 * Phase 9.4 — 🎙️ Host Stats Card (Creator Dashboard)
 * --------------------------------------------------
 * Surfaces the user's lifetime + recent live-hosting performance
 * (lives streamed, gifts earned, listener peak, hours streamed, top
 * supporter, best live, eligibility) so creators can see how their
 * hosting effort is paying off.
 *
 * Data source: GET /api/host/me/stats (authed).
 * Render is gated: shows nothing if the user has zero lifetime lives
 * AND is not yet eligible — avoids cluttering brand-new accounts.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtHours = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h <= 0 && m <= 0) return "0m";
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
};

const StatTile = ({ label, value, sub, emoji, testid }) => (
  <div
    className="bg-black/40 border border-pink-500/20 rounded-xl p-4"
    data-testid={testid}
  >
    <p className="text-[10px] uppercase tracking-wider text-pink-300/70 font-bold mb-1">
      {emoji} {label}
    </p>
    <p className="text-2xl font-black text-white leading-tight">{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

const HostStatsCard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/host/me/stats");
        if (!cancelled) setStats(r.data);
      } catch (_e) {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!stats) return null;

  const life = stats.lifetime || {};
  const last7 = stats.last_7_days || {};
  const last30 = stats.last_30_days || {};
  const eligibility = stats.eligibility || {};
  const best = stats.best_live;
  const top = stats.top_supporter;

  const hasActivity = (life.total_lives || 0) > 0;
  const isDormant = eligibility.eligible && hasActivity && (last7.lives || 0) === 0;
  const isFirstTimer = eligibility.eligible && !hasActivity;

  // Ineligible AND no past activity → render locked-only invite card.
  // (If they have past activity, fall through to the full stats card so they
  //  can still see their lifetime numbers — the locked banner replaces the
  //  Go Live CTA inline.)
  if (!eligibility.eligible && !hasActivity) {
    return (
      <Card
        className="bg-gradient-to-br from-pink-900/30 via-[#1a1a2e] to-purple-900/20 border border-pink-500/30 p-5 sm:p-6"
        data-testid="host-stats-empty-card"
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg shadow-pink-500/30 shrink-0">
            <span className="text-2xl">🔒</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-white">Live Hosting Locked</h3>
            <p
              className="text-sm text-amber-200/90 mt-1.5 leading-relaxed"
              data-testid="host-stats-locked-message"
            >
              Unlock hosting by getting verified, earning features, or admin approval.
            </p>
            <div className="mt-4 flex gap-2 flex-wrap">
              <Link to="/profile/edit">
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white text-xs font-bold"
                  data-testid="host-stats-get-verified-btn"
                >
                  ✓ Apply for Verification
                </Button>
              </Link>
              <Link to="/beams">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-pink-500/50 text-pink-200 hover:bg-pink-500/20 text-xs"
                  data-testid="host-stats-explore-beams-btn"
                >
                  Explore Live Beams →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className="bg-gradient-to-br from-pink-900/40 via-[#1a1a2e] to-purple-900/30 border border-pink-500/40 p-6 shadow-xl shadow-pink-500/10"
      data-testid="host-stats-card"
    >
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg shadow-pink-500/30">
            <span className="text-2xl">🎙️</span>
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Live Hosting Stats</h3>
            <p className="text-sm text-pink-300/80 font-medium">
              Your live-beam performance · updated in real time
            </p>
          </div>
        </div>
        <Link to="/live" data-testid="host-stats-rankings-link">
          <Button
            variant="outline"
            size="sm"
            className="border-pink-500/50 text-pink-200 hover:bg-pink-500/20 whitespace-nowrap"
          >
            🏆 View Rankings →
          </Button>
        </Link>
      </div>

      {/* 🎙️ Go Live CTA — eligible: Go Live (dormant variant if 7d+ idle).
          Ineligible-with-history: locked banner replaces the CTA inline. */}
      {!eligibility.eligible ? (
        <div
          className="mb-5 rounded-xl border border-amber-400/40 bg-gradient-to-r from-amber-500/10 to-pink-500/10 p-4 flex items-center gap-3 sm:gap-4 flex-wrap sm:flex-nowrap"
          data-testid="host-stats-locked-banner"
        >
          <div className="text-2xl shrink-0">🔒</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white">Live Hosting Locked</p>
            <p
              className="text-xs text-amber-200/90 mt-0.5 leading-relaxed"
              data-testid="host-stats-locked-message"
            >
              Unlock hosting by getting verified, earning features, or admin approval.
            </p>
          </div>
          <Link to="/profile/edit" className="w-full sm:w-auto">
            <Button
              size="sm"
              className="w-full sm:w-auto bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white text-xs font-bold"
              data-testid="host-stats-get-verified-btn"
            >
              ✓ Apply for Verification
            </Button>
          </Link>
        </div>
      ) : (
        <div
          className={`mb-5 rounded-xl border p-4 flex items-center gap-3 sm:gap-4 flex-wrap sm:flex-nowrap ${
            isDormant
              ? "bg-gradient-to-r from-amber-500/15 via-pink-500/15 to-purple-500/15 border-amber-400/40"
              : "bg-gradient-to-r from-pink-500/10 to-purple-500/10 border-pink-500/30"
          }`}
          data-testid={isDormant ? "host-go-live-dormant" : "host-go-live-cta"}
        >
          <div className="text-2xl shrink-0">{isDormant ? "📡" : "🎙️"}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white">
              {isDormant
                ? "Your fans haven't seen you live lately"
                : isFirstTimer
                ? "Ready for your first live beam?"
                : "Ready to go live?"}
            </p>
            <p className="text-xs text-pink-200/80 mt-0.5">
              {isDormant
                ? "Drop a beam now to keep the gifts and rankings momentum going."
                : isFirstTimer
                ? "Open a Beam, invite guests, and start earning live gifts."
                : "Open a Beam Room and connect with your fans in real time."}
            </p>
          </div>
          <Link to="/beams" className="w-full sm:w-auto">
            <Button
              className={`w-full sm:w-auto font-bold text-white shadow-lg ${
                isDormant
                  ? "bg-gradient-to-r from-amber-500 to-pink-600 hover:from-amber-400 hover:to-pink-500 shadow-amber-500/30"
                  : "bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 shadow-pink-500/30"
              }`}
              data-testid="host-go-live-btn"
            >
              {isDormant ? "Go Live Now →" : "🎙️ Go Live →"}
            </Button>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatTile
          emoji="🎙️"
          label="Lifetime Lives"
          value={fmt(life.total_lives)}
          sub={`${fmt(last30.lives || 0)} in last 30 days`}
          testid="host-stat-total-lives"
        />
        <StatTile
          emoji="🎁"
          label="Live Gifts (coins)"
          value={fmt(life.total_gift_coins)}
          sub={`${fmt(last7.gift_coins || 0)} this week`}
          testid="host-stat-total-gifts"
        />
        <StatTile
          emoji="👥"
          label="Listener Peaks"
          value={fmt(life.total_listener_peak)}
          sub="Summed across all lives"
          testid="host-stat-listener-peak"
        />
        <StatTile
          emoji="⏱️"
          label="Hours Streamed"
          value={`${life.total_live_hours || 0}h`}
          sub={`${fmtHours(last30.live_seconds || 0)} in last 30d`}
          testid="host-stat-total-hours"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Best Live */}
        {best ? (
          <div
            className="bg-black/40 border border-pink-500/20 rounded-xl p-4"
            data-testid="host-stats-best-live"
          >
            <p className="text-[10px] uppercase tracking-wider text-pink-300/70 font-bold mb-1">
              🏆 Best-Performing Live
            </p>
            <p className="text-base font-bold text-white truncate">
              {best.title || "Untitled live"}
            </p>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
              <span>🪙 {fmt(best.gift_total_coins)} coins</span>
              <span>👥 {fmt(best.participant_count)} peak</span>
            </div>
          </div>
        ) : (
          <div
            className="bg-black/30 border border-pink-500/10 rounded-xl p-4 text-xs text-gray-500"
            data-testid="host-stats-best-live-empty"
          >
            🏆 Your best-performing live will appear here after your first stream.
          </div>
        )}

        {/* Top supporter */}
        {top ? (
          <div
            className="bg-black/40 border border-pink-500/20 rounded-xl p-4"
            data-testid="host-stats-top-supporter"
          >
            <p className="text-[10px] uppercase tracking-wider text-pink-300/70 font-bold mb-2">
              💜 Top Live Supporter
            </p>
            <div className="flex items-center gap-3">
              <Link to={`/artist/${top.user_id}`} className="shrink-0">
                <img
                  src={getProfilePhotoUrl(top)}
                  alt={top.artist_name || "supporter"}
                  className="w-10 h-10 rounded-full object-cover border-2 border-pink-500/40"
                />
              </Link>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/artist/${top.user_id}`}
                  className="text-white font-semibold text-sm hover:text-pink-200 truncate block"
                >
                  {top.artist_name || "Anonymous"}
                </Link>
                <p className="text-xs text-pink-300/80">
                  🪙 {fmt(top.coins)} coins · {fmt(top.gifts)} gifts
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="bg-black/30 border border-pink-500/10 rounded-xl p-4 text-xs text-gray-500"
            data-testid="host-stats-top-supporter-empty"
          >
            💜 Your top live supporter will appear here once fans start gifting.
          </div>
        )}
      </div>

      {/* Eligibility chip */}
      {eligibility.eligible && eligibility.reasons?.length > 0 && (
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="host-stats-eligibility-chips">
          <span className="text-[10px] uppercase tracking-wider text-pink-300/60 font-bold">
            Host status:
          </span>
          {eligibility.reasons.map((r) => (
            <span
              key={r}
              className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-pink-500/20 border border-pink-500/40 text-pink-200"
              data-testid={`host-eligibility-${r}`}
            >
              ✓ {r.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
};

export default HostStatsCard;
